import { WorkExperienceCompany, WorkExperienceRole, WorkExperienceBullet } from '@applicator/shared'
import React from 'react'

import { useDatePickerPositionEffect, useDropdownListenersEffect } from './useWorkExperienceRoleHandlers/effects'
import { createDatePickerPosition } from './useWorkExperienceRoleHandlers/datePickerHelpers'
import { handleTogglePosition, handleToggleLocation, handleToggleBullet, handleUpdateEmploymentType, handleDeleteRole, handleDeleteBullet, handleReorderBullets, handleUpdateRole, handleUpdateBullet, handleStartEditRole, handleSaveRole, handleStartEditRoleLocation, handleSaveRoleLocation, handleSaveBullet as persistBulletEdit, handleDateChange as persistDateChange, handleDatePickerClose as closeDatePickerState } from './useWorkExperienceRoleHandlers/handlers'
import { normalizeDateValue } from './useWorkExperienceRoleHandlers/helpers'

export function useWorkExperienceRoleHandlers(
  companies: WorkExperienceCompany[],
  onChange: (data: WorkExperienceCompany[]) => void,
  editingRolePosition: string,
  editingRoleLocation: string,
  editingBulletText: string,
  editingBulletId: string | null,
  datePickerState: {
    companyId: string
    roleId: string
    field: 'startDate' | 'endDate'
    position: { top: number; left: number; width: number }
    triggerKey: string
  } | null,
  openTypeDropdown: { companyId: string; roleId: string } | null,
  dateFieldRefs: React.MutableRefObject<{ [key: string]: HTMLElement | null }>,
  typeDropdownRefs: React.MutableRefObject<{ [key: string]: HTMLElement | null }>,
  setEditingRoleId: (id: string | null) => void,
  setEditingRoleLocationId: (id: string | null) => void,
  setEditingBulletId: (id: string | null) => void,
  setEditingBulletText: (text: string) => void,
  setEditingRolePosition: (value: string) => void,
  setEditingRoleLocation: (value: string) => void,
  setDatePickerState: (state: {
    companyId: string
    roleId: string
    field: 'startDate' | 'endDate'
    position: { top: number; left: number; width: number }
    triggerKey: string
  } | null) => void,
  setOpenTypeDropdown: (state: { companyId: string; roleId: string } | null) => void,
  setEditingRoleDate: (field: 'startDate' | 'endDate', value: string) => void,
  datePickerRef: React.RefObject<HTMLElement | null>,
  scrollRootRef?: React.RefObject<HTMLElement | null>,
) {

  const handleTogglePositionWrapper = (companyId: string, roleId: string) => {
    handleTogglePosition(companyId, roleId, companies, onChange)
  }

  const handleToggleLocationWrapper = (companyId: string, roleId: string) => {
    handleToggleLocation(companyId, roleId, companies, onChange)
  }

  const handleToggleBulletWrapper = (companyId: string, roleId: string, bulletId: string) => {
    handleToggleBullet(companyId, roleId, bulletId, companies, onChange)
  }

  const handleUpdateEmploymentTypeWrapper = (companyId: string, roleId: string, employmentType: string) => {
    handleUpdateEmploymentType(companyId, roleId, employmentType, companies, onChange, setOpenTypeDropdown)
  }

  // Helper function to stop event propagation
  const stopEventPropagation = (event: React.MouseEvent) => {
    event.stopPropagation()
  }

  // Helper function to get trigger element from event
  const getTriggerElementFromEvent = (event: React.MouseEvent): HTMLElement => {
    return event.currentTarget as HTMLElement
  }

  // Helper function to build trigger key
  const buildTriggerKey = (companyId: string, roleId: string): string => {
    return `${companyId}-${roleId}-type`
  }

  // Helper function to store trigger element in refs
  const storeTriggerElementInRefs = (triggerKey: string, triggerElement: HTMLElement) => {
    typeDropdownRefs.current[triggerKey] = triggerElement
  }

  // Helper function to set open type dropdown state
  const setOpenTypeDropdownState = (companyId: string, roleId: string) => {
    setOpenTypeDropdown({ companyId, roleId })
  }

  const handleOpenTypeDropdown = (companyId: string, roleId: string, event: React.MouseEvent) => {
    stopEventPropagation(event)
    const triggerElement = getTriggerElementFromEvent(event)
    const triggerKey = buildTriggerKey(companyId, roleId)
    storeTriggerElementInRefs(triggerKey, triggerElement)
    setOpenTypeDropdownState(companyId, roleId)
  }

  const handleCloseTypeDropdown = () => {
    setOpenTypeDropdown(null)
  }

  const handleDeleteRoleWrapper = (companyId: string, roleId: string) => {
    handleDeleteRole(companyId, roleId, companies, onChange)
  }

  const handleDeleteBulletWrapper = (companyId: string, roleId: string, bulletId: string) => {
    handleDeleteBullet(companyId, roleId, bulletId, companies, onChange)
  }

  const handleReorderBulletsWrapper = (companyId: string, roleId: string, draggedBulletId: string, targetIndex: number) => {
    handleReorderBullets(companyId, roleId, draggedBulletId, targetIndex, companies, onChange)
  }

  const handleUpdateRoleWrapper = (
    companyId: string,
    roleId: string,
    field: 'position' | 'location' | 'employmentType' | 'startDate' | 'endDate',
    value: string
  ) => {
    handleUpdateRole(companyId, roleId, field, value, companies, onChange)
  }

  const handleUpdateBulletWrapper = (companyId: string, roleId: string, bulletId: string, text: string) => {
    handleUpdateBullet(companyId, roleId, bulletId, text, companies, onChange)
  }

  const handleStartEditRoleWrapper = (role: WorkExperienceRole) => {
    handleStartEditRole(role, setEditingRoleId, setEditingRolePosition)
  }

  const handleSaveRoleWrapper = (companyId: string, roleId: string) => {
    handleSaveRole(companyId, roleId, editingRolePosition, companies, onChange, setEditingRoleId)
  }

  const handleStartEditRoleLocationWrapper = (role: WorkExperienceRole) => {
    handleStartEditRoleLocation(role, setEditingRoleLocationId, setEditingRoleLocation)
  }

  const handleSaveRoleLocationWrapper = (companyId: string, roleId: string) => {
    handleSaveRoleLocation(companyId, roleId, editingRoleLocation, companies, onChange, setEditingRoleLocationId)
  }

  const initializeDatePicker = (role: WorkExperienceRole, companyId: string, field: 'startDate' | 'endDate', event: React.MouseEvent) => {
    stopEventPropagation(event)
    const triggerElement = event.currentTarget as HTMLElement
    const triggerKey = `${companyId}-${role.id}-${field}`
    dateFieldRefs.current[triggerKey] = triggerElement
    const position = createDatePickerPosition(triggerElement, { monthYearOnly: true })
    setDatePickerState({
      companyId,
      roleId: role.id,
      field,
      position,
      triggerKey,
    })
  }

  const handleStartEditRoleStartDate = (role: WorkExperienceRole, companyId: string, event: React.MouseEvent) => {
    setEditingRoleDate('startDate', role.startDate || '')
    initializeDatePicker(role, companyId, 'startDate', event)
  }

  const handleStartEditRoleEndDate = (role: WorkExperienceRole, companyId: string, event: React.MouseEvent) => {
    setEditingRoleDate('endDate', role.endDate || '')
    initializeDatePicker(role, companyId, 'endDate', event)
  }

  const handleDateChange = (date: string) => {
    if (!datePickerState) return
    const dateValue = normalizeDateValue(date)
    setEditingRoleDate(datePickerState.field, dateValue)
    persistDateChange(date, datePickerState, companies, onChange, setDatePickerState)
  }

  const handleDatePickerClose = () => {
    closeDatePickerState(setDatePickerState)
  }

  useDatePickerPositionEffect(
    datePickerState,
    dateFieldRefs,
    setDatePickerState,
    datePickerRef,
    scrollRootRef,
  )
  useDropdownListenersEffect(openTypeDropdown, typeDropdownRefs, handleCloseTypeDropdown)

  const handleStartEditBullet = (bullet: WorkExperienceBullet) => {
    setEditingBulletId(bullet.id)
    setEditingBulletText(bullet.text)
  }

  // Helper function to find bullet in role
  const findBulletInRole = (role: WorkExperienceRole, bulletId: string): WorkExperienceBullet | undefined => {
    return (role.bulletPoints || []).find((b) => b.id === bulletId)
  }

  // Helper function to find bullet in company
  const findBulletInCompany = (company: WorkExperienceCompany, bulletId: string): WorkExperienceBullet | undefined => {
    for (const role of company.roles || []) {
      const bullet = findBulletInRole(role, bulletId)
      if (bullet) return bullet
    }
    return undefined
  }

  // Helper function to find bullet by ID across all companies
  const findBulletById = (bulletId: string | null): WorkExperienceBullet | undefined => {
    if (!bulletId) return undefined
    for (const company of companies) {
      const bullet = findBulletInCompany(company, bulletId)
      if (bullet) return bullet
    }
    return undefined
  }

  const handleCancelEditBullet = () => {
    const bullet = findBulletById(editingBulletId)
    if (bullet) {
      setEditingBulletText(bullet.text)
    }
    setEditingBulletId(null)
  }

  const handleSaveBullet = (companyId: string, roleId: string, bulletId: string) => {
    persistBulletEdit(
      companyId,
      roleId,
      bulletId,
      editingBulletText,
      companies,
      onChange,
      setEditingBulletId
    )
  }

  return {
    handleTogglePosition: handleTogglePositionWrapper,
    handleToggleLocation: handleToggleLocationWrapper,
    handleToggleBullet: handleToggleBulletWrapper,
    handleUpdateEmploymentType: handleUpdateEmploymentTypeWrapper,
    handleOpenTypeDropdown,
    handleCloseTypeDropdown,
    handleDeleteRole: handleDeleteRoleWrapper,
    handleDeleteBullet: handleDeleteBulletWrapper,
    handleReorderBullets: handleReorderBulletsWrapper,
    handleUpdateRole: handleUpdateRoleWrapper,
    handleUpdateBullet: handleUpdateBulletWrapper,
    handleStartEditRole: handleStartEditRoleWrapper,
    handleSaveRole: handleSaveRoleWrapper,
    handleStartEditRoleLocation: handleStartEditRoleLocationWrapper,
    handleSaveRoleLocation: handleSaveRoleLocationWrapper,
    handleStartEditRoleStartDate,
    handleStartEditRoleEndDate,
    handleDateChange,
    handleDatePickerClose,
    handleStartEditBullet,
    handleCancelEditBullet,
    handleSaveBullet,
  }
}
